import { TEST_IDS } from '@va/constants';
import { CommentsIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button, fontWeights, paragraphSizes, ParagraphWithTooltip, RadioInputButton } from '@va/ui/design-system';
import { TooltipWrapper, useTooltipContext } from '@va/ui/tooltips';
import { DropdownArrow } from '@va/util/components';
import { useIsolatedFocusedContainer } from '@va/util/misc';
import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';

type SelectItemsPerPageDropdownProps = {
  pageSize: number;
  setPageSize: (newPageSize: number) => void;
  total: number;
  options?: Array<number>;
  disabled?: boolean;
  size?: 'small' | 'large';
};

export const SelectItemsPerPageDropdown = ({
  pageSize,
  setPageSize,
  total,
  options = [6, 12, 18, 24, 36, 48],
  disabled = false,
  size = 'large',
}: SelectItemsPerPageDropdownProps) => {
  const translate = useTranslate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isDropdownVisible, setDropdownVisible] = useState(false);

  const pageSizeOptions = useMemo(
    () =>
      options.map((option) => {
        return { label: translate(`pagination.pageSize.itemsCount`, { items: option }), value: option };
      }),
    [translate, options],
  );

  return (
    <TooltipWrapper content={translate('tooltip.label.selectPageSize')}>
      <div>
        <Button
          ref={buttonRef}
          text={pageSize}
          bottomIcon={
            <DropdownArrow
              iconClassName={classNames({
                'w-1.5': size === 'small',
                'w-2.5 sm:w-1.5': size === 'large',
              })}
              open={isDropdownVisible}
              color='#696969'
            />
          }
          color='tertiary'
          tooltip={
            <DropdownOptions
              selectedOption={pageSize}
              options={pageSizeOptions}
              onChange={(value: number) => {
                setPageSize(value);
                setDropdownVisible(false);
              }}
              total={total}
            />
          }
          tooltipProps={{
            trigger: 'click',
            tooltipClassNames: 'rounded-18! w-[15rem] border-2 border-gray-concrete',
            arrow: false,
            open: isDropdownVisible,
            onOpenChange: (open) => {
              if (!open) buttonRef.current?.focus();
              setDropdownVisible(open);
            },
            useDefaultStyle: false,
            disabled: disabled,
          }}
          className={classNames('cursor-pointer text-xs! sm:w-9 sm:h-9 sm:p-9px sm:rounded-9px', {
            'w-9 h-9 px-1.5!': size === 'small',
          })}
          bottomIconWrapperCss='sm:bottom-0.5'
          disabled={disabled}
          data-testid={TEST_IDS.generic.table.rowsPerPageDropdown}
          size={size}
        />
      </div>
    </TooltipWrapper>
  );
};

const DropdownOptions = ({
  selectedOption,
  options,
  onChange,
  total = 0,
}: {
  selectedOption: number;
  options: { label: string | number; value: number }[];
  onChange: (value: number) => void;
  total?: number;
}) => {
  const { context } = useTooltipContext();
  const containerRef = useRef<HTMLDivElement>(null);

  useIsolatedFocusedContainer({ containerRef, enabled: context.open });

  return (
    <div
      ref={containerRef}
      className='flex flex-col bg-white p-2 rounded-18px'
      data-testid={TEST_IDS.generic.table.rowsPerPageTooltip}
    >
      {options.map(({ label, value }, index, arr) => {
        const isSelected = selectedOption === value;
        const isDisabled = total < value && index > 0 && total < arr[index - 1]?.value;
        return (
          <button
            key={value}
            className={classNames('flex items-center justify-between p-3 rounded-xl truncate', {
              'opacity-50 cursor-not-allowed': isDisabled,
              'hover:bg-gray-concrete active:bg-gray-mercury cursor-pointer': !isDisabled,
            })}
            onClick={() => !isDisabled && onChange(value)}
            data-testid={`${TEST_IDS.generic.table.rowsPerPageOption}-${value}`}
          >
            <div className='flex items-center truncate'>
              <CommentsIcon className='shrink-0' color='#969696' />
              <ParagraphWithTooltip
                colorClassName='text-gray-charcoal'
                className='ml-9px truncate'
                size={paragraphSizes.tiny}
                weight={fontWeights.medium}
              >
                {label}
              </ParagraphWithTooltip>
            </div>
            <RadioInputButton selected={isSelected} className='w-6 h-6 ml-3 shrink-0' />
          </button>
        );
      })}
    </div>
  );
};
